import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnInit} from "@angular/core";
import {PricePlansHttpService} from "../../services/price-plans-http.service";
import {PricePlanDetailsModel} from "../../models/price-plan-details.model";
import {CommonsFacadeService} from "../../../../commons/angular/state/commons/commons-facade.service";
import {RouterService} from "../../../../commons/angular/services/router.service";
import {ExportPricePlanPayloadModel} from "../../models/export-price-plan-payload.model";
import {TranslateService} from "@ngx-translate/core";
import {ExportType} from "../../../../commons/types/export.type";
import {ContractPriceType} from "../../../../contracts/types/contract-price.type";
import {ContractPriceMethodType} from "../../../../contracts/types/contract-price-method.type";

require('./price-plan-bundle-state-view.scss');
@Component({
  standalone: false,
  selector: 'app-price-plan-bundle-state-view',
  templateUrl: './price-plan-bundle-state-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PricePlanBundleStateViewComponent implements OnInit {

  @Input() pricePlansBundleHierarchy: any;
  @Input() selectedPricePlanId: string;

  public pricePlanDetails: PricePlanDetailsModel;
  public selectedAccountIds: string[] = [];

  constructor(@Inject(PricePlansHttpService) private pricePlansHttpService: PricePlansHttpService, @Inject(CommonsFacadeService) private commonsFacadeService: CommonsFacadeService,
              @Inject(RouterService) private routerService: RouterService, @Inject('pricePlanBundleActionsService') private pricePlanBundleActionsService,
              @Inject('pricePlanDialogService') private pricePlanDialogService, @Inject('pricePlanBundleService') private pricePlanBundleService,
              @Inject('accountDialogService') private accountDialogService, @Inject(TranslateService) private translateService: TranslateService,
              @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit() {
    if (!!this.selectedPricePlanId) {
      this.getPricePlanDetails(this.selectedPricePlanId);
    }
  }

  onSelectPricePlan(selectedPricePlanId) {
    if (this.selectedPricePlanId === selectedPricePlanId) {
      return;
    }
    this.selectedPricePlanId = selectedPricePlanId;
    this.pricePlanDetails = undefined;
    this.getPricePlanDetails(selectedPricePlanId);
  }

  onViewPricePlanDrillDown(pricePlanId) {
    this.routerService.routeToNewTab('pricePlanView.details.service', {pricePlanBundleId: pricePlanId});
  }

  onSetPricePlanAsDefault(pricePlanBundle) {
    this.pricePlanBundleActionsService.setAsDefault(undefined, pricePlanBundle.id, pricePlanBundle.name)
      .then(() => this.getHierarchy())
  };

  onDuplicatePricePlan(pricePlanBundle) {
    this.pricePlanBundleActionsService.duplicateBundle(undefined, pricePlanBundle)
      .then(() => this.getHierarchy())
  };

  onCreatePricePlanChild(pricePlanBundle) {
    this.pricePlanBundleActionsService.createChildBundle(undefined, pricePlanBundle.id)
      .then(() => this.getHierarchy())
  }

  onEditPricePlan(pricePlanBundle) {
    this.pricePlanDialogService.editProgram(undefined, pricePlanBundle)
      .then((updatedBundle) => {
        this.pricePlanBundleService.updatePricePlanBundle(updatedBundle)
          .then(() => {
            this.getPricePlanDetails(pricePlanBundle.id);
          });
      })
  }

  onUpdatePricePlanAccounts(pricePlanBundle) {
    const accountIds = pricePlanBundle.accountsDetails.map(a => a.id);
    this.accountDialogService.addDemandAccounts(undefined, accountIds, false)
      .then((payload) => {
        const accountIdsToAdd = payload.ids.filter(id => !accountIds.includes(id));
        this.updatePricePlanBundleAccounts(pricePlanBundle.id, accountIdsToAdd);
      })
  }

  onImportProgramSettings() {
    this.routerService.routeTo('pricePlans-import');
  }

  onExport(exportType: ExportType, event) {
    const accountIds: string[] = this.selectedAccountIds?.length ?
      this.selectedAccountIds :
      (this.pricePlanDetails?.accountsDetails || []).map((account) => {
        return account.id;
      })
    const payload: ExportPricePlanPayloadModel = {
      accountIds: accountIds,
      exportType: exportType,
      priceMethodType: ContractPriceMethodType.Locations,
      priceType: ContractPriceType.ByPackageSize,
      fieldTranslationMap: {
        accountExternalId: this.translateService.instant('EXPORT_PRICE_PLAN_COLUMNS.accountExternalId'),
        accountName: this.translateService.instant('EXPORT_PRICE_PLAN_COLUMNS.accountName'),
        programType: this.translateService.instant('EXPORT_PRICE_PLAN_COLUMNS.programType'),
        originLocationType: this.translateService.instant('EXPORT_PRICE_PLAN_COLUMNS.originLocationType'),
        originLocationName: this.translateService.instant('EXPORT_PRICE_PLAN_COLUMNS.originLocationName'),
        destinationLocationType: this.translateService.instant('EXPORT_PRICE_PLAN_COLUMNS.destinationLocationType'),
        destinationLocationName: this.translateService.instant('EXPORT_PRICE_PLAN_COLUMNS.destinationLocationName'),
        packageType: this.translateService.instant('EXPORT_PRICE_PLAN_COLUMNS.packageType'),
        price: this.translateService.instant('EXPORT_PRICE_PLAN_COLUMNS.price')
      },
    }

    this.pricePlanBundleService.exportPrograms(this.selectedPricePlanId, payload)
      .then((res) => {

      });
  }

  onSelectedAccountIdsChange(selectedAccountIds: string[]) {
    this.selectedAccountIds = selectedAccountIds;
  }

  private getHierarchy() {
    this.pricePlanBundleService.getHierarchy()
      .then((hierarchy) => {
        this.pricePlansBundleHierarchy = hierarchy;
        this.cdr.detectChanges();
      })
  }

  private updatePricePlanBundleAccounts(pricePlanId: string, accountIds: string[]) {
    this.pricePlanBundleService.updatePricePlanAccounts(pricePlanId, accountIds)
      .then((res) => {
        this.getPricePlanDetails(pricePlanId)
      });
  }

  private getPricePlanDetails(pricePlanId) {
    this.commonsFacadeService.setLoaderOn();
    this.pricePlansHttpService.getPricePlanDetails(pricePlanId).subscribe({
      next: (pricePlanDetails) => {
        this.pricePlanDetails = pricePlanDetails;
      },
      complete: () => {
        this.commonsFacadeService.setLoaderOff();
        this.cdr.detectChanges();
      }
    })
  }
}
